import React, { useEffect, useRef, useState } from "react";
import { Loader2, X, Mail, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PasswordInput from "@/components/site/PasswordInput";

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return detail.msg || String(detail);
};

const STEP = { REQUEST: 0, CONFIRM: 1, DONE: 2 };

const ForgotPasswordModal = ({ open, onClose, defaultEmail = "" }) => {
  const [step, setStep] = useState(STEP.REQUEST);
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [devCode, setDevCode] = useState("");
  const closeBtn = useRef(null);
  const prevOpen = useRef(false);

  useEffect(() => {
    // Only reset when transitioning from closed → open
    if (open && !prevOpen.current) {
      setStep(STEP.REQUEST);
      setEmail(defaultEmail);
      setCode("");
      setPw("");
      setPw2("");
      setErr("");
      setDevCode("");
    }
    prevOpen.current = open;
  }, [open, defaultEmail]);

  if (!open) return null;

  const sendCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      if (data?.dev_code) setDevCode(data.dev_code);
      setStep(STEP.CONFIRM);
      toast.success("If that email is on file, a reset code was sent.");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/password-reset/confirm", { email, code, new_password: pw }, { withCredentials: true });
      setStep(STEP.DONE);
      toast.success("Password reset. You're now signed in.");
      window.setTimeout(() => { window.location.reload(); }, 1200);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="forgot-modal">
      <div className="w-full max-w-md bg-spike-elevated border border-white/10 rounded-2xl p-7 relative">
        <button ref={closeBtn} type="button" onClick={onClose} className="absolute top-3 right-3 text-spike-dim hover:text-spike-text" data-testid="forgot-close-btn"><X className="w-5 h-5"/></button>
        <div className="inline-flex items-center gap-2 text-xs font-orbitron font-bold text-spike-blue-light tracking-[0.3em] uppercase"><KeyRound className="w-3.5 h-3.5"/> Reset Password</div>
        <h2 className="mt-2 font-orbitron text-2xl font-black uppercase text-spike-text">
          {step === STEP.REQUEST && <>Forgot your <span className="text-spike-blue-light">password?</span></>}
          {step === STEP.CONFIRM && <>Check your <span className="text-spike-blue-light">inbox.</span></>}
          {step === STEP.DONE && <>Password <span className="text-spike-blue-light">reset.</span></>}
        </h2>

        {err && <p data-testid="forgot-error" className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-3">{err}</p>}

        {step === STEP.REQUEST && (
          <form onSubmit={sendCode} className="mt-5 space-y-3" data-testid="forgot-form-request">
            <p className="text-sm text-spike-text-muted font-exo">Enter your account email — we&apos;ll send you a 6-digit reset code.</p>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email" className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text"/>
            </div>
            <button type="submit" disabled={busy} data-testid="forgot-send-btn" className="w-full inline-flex justify-center items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron font-bold uppercase text-xs tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send Reset Code"}
            </button>
          </form>
        )}

        {step === STEP.CONFIRM && (
          <form onSubmit={confirmReset} className="mt-5 space-y-3" data-testid="forgot-form-confirm">
            <p className="text-sm text-spike-text-muted font-exo">Enter the 6-digit code we emailed you and choose a new password.</p>
            {devCode && (
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="forgot-dev-code">
                DEV preview only — code: <strong className="font-mono">{devCode}</strong>
              </p>
            )}
            <input type="text" inputMode="numeric" pattern="[0-9]*" required value={code} maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" data-testid="forgot-code-input" className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2.5 text-sm text-spike-text tracking-[0.5em] font-mono text-center"/>
            <PasswordInput
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="New password (min 8 chars)"
              data-testid="forgot-new-pw"
            />
            <PasswordInput
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm new password"
              data-testid="forgot-new-pw2"
            />
            <button type="submit" disabled={busy} data-testid="forgot-confirm-btn" className="w-full inline-flex justify-center items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron font-bold uppercase text-xs tracking-[0.2em] px-5 py-3 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Reset Password"}
            </button>
            <button type="button" onClick={sendCode} disabled={busy} data-testid="forgot-resend-btn" className="w-full text-xs text-spike-blue-light hover:underline">Resend code</button>
          </form>
        )}

        {step === STEP.DONE && (
          <p className="mt-4 text-sm text-spike-text font-exo">Done — you&apos;re signed in. Reloading…</p>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
